'use client';

import { KeyboardEvent, ChangeEvent, FC, ReactElement, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import { generateUuid } from '@lib/uuid';
import { getBgClassNames, getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  PortalBgType,
  PortalBorderType,
  PortalClassState,
  PortalStyleConfig,
  PortalTextType,
  ThemeColorSchemas,
} from '@nx-frontend/shared/types';
import { UiLabel } from './label';

export enum UiInputView {
  LABEL_TOP,
  LABEL_BORDER,
}

export enum UiIconSide {
  LEFT,
  RIGHT,
  NO,
}

interface UiInputProps {
  value: string | number;
  id?: string;
  name?: string;
  type?: 'text' | 'number' | 'email' | 'password' | 'url' | 'date' | 'datetime-local';
  label?: string;
  placeholder?: string;
  labelSmallGrey?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  icon?: ReactElement;
  iconSide?: UiIconSide | undefined;
  iconClassName?: string;
  iconSecond?: ReactElement;
  iconSecondSide?: UiIconSide | undefined;
  onIconSecondClick?: () => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  size?: UiInputSize;
  view?: UiInputView;
  inputRef?: React.Ref<HTMLInputElement> | null;
  maxLength?: number | undefined;
  themeColors?: ThemeColorSchemas;
  themeStyle?: PortalStyleConfig;
  className?: string;
  inputClassName?: string;
  cursor?: string;
  required?: true | false;
  min?: string;
  max?: string;
  step?: number;
  spellCheck?: boolean;
  autoFocus?: boolean;
  /** Called after pressing Enter or on blur. */
  onCommit?: (value: string) => void;
  labelInfoIcon?: boolean;
  labelInfoIconTooltipContent?: string | ReactElement;
}

export enum UiInputSize {
  SMALL = 'px-2 py-1.5',
  DEFAULT = 'px-2 py-3',
  LARGE = 'px-3 py-4',
  MEDIUM = 'px-2 py-2',
}

export const UiInput: FC<UiInputProps> = ({
  id: propsId,
  type = 'text',
  label,
  value,
  name,
  placeholder,
  icon,
  iconSide = UiIconSide.NO,
  iconClassName = '',
  iconSecond,
  iconSecondSide = UiIconSide.NO,
  onIconSecondClick,
  error,
  errorText,
  disabled,
  labelSmallGrey,
  onChange = () => {
    return;
  },
  onBlur,
  onFocus,
  onKeyDown,
  size = UiInputSize.DEFAULT,
  view = UiInputView.LABEL_BORDER,
  maxLength,
  inputRef = null,
  themeColors,
  themeStyle,
  className,
  inputClassName,
  cursor = '',
  required,
  min,
  max,
  spellCheck,
  autoFocus = false,
  step = 1,
  onCommit,
  labelInfoIcon,
  labelInfoIconTooltipContent,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const id = useMemo(() => {
    return propsId != null ? propsId : generateUuid();
  }, [propsId]);

  useEffect(() => setInputValue(value), [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setInputValue(e.target.value);
    onChange(e);
  };

  const handleFocus = (e: ChangeEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
    onCommit?.(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(e);
    if (e.key === 'Enter') {
      onCommit?.(e.currentTarget.value);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const boarderColor =
    themeColors && themeStyle
      ? classNames(...getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY))
      : 'border-cyan-900 hover:border-cyan-700';

  const bgColor = themeColors ? classNames(getBgClassNames(themeColors, PortalBgType.SURFACE)) : 'bg-white';
  const textColor = themeColors ? classNames(getTextClassNames(themeColors, PortalTextType.SURFACE)) : 'text-black';

  const boarderFocusColor =
    themeColors && themeStyle
      ? classNames(
          ...getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY, {
            states: [PortalClassState.FOCUS],
          })
        )
      : 'focus:border-cyan-900';

  const caret = themeColors && themeStyle ? 'caret-black-500' : 'caret-cyan-900';

  const inputTextColorClassName = !disabled ? 'text-gray-900' : 'text-gray-500';

  const iconSecondInnerClassname =
    themeColors && iconSecond
      ? classNames(
          getTextClassNames(themeColors, PortalTextType.SURFACE_3),
          getBgClassNames(themeColors, PortalBgType.SURFACE_INNER_HOVER, [PortalClassState.HOVER])
        )
      : '';

  return (
    <div className={className}>
      {view !== UiInputView.LABEL_BORDER && (
        <UiLabel
          id={id}
          label={label}
          disabled={disabled}
          error={error}
          view={view}
          isFocused={isFocused}
          labelSmallGrey={labelSmallGrey}
          themeColors={themeColors}
          themeStyle={themeStyle}
          infoIcon={labelInfoIcon}
          infoIconTooltipContent={labelInfoIconTooltipContent}
        />
      )}
      <div className="flex relative mb-2">
        {iconSide === UiIconSide.LEFT && icon && (
          <div
            className={classNames(
              iconClassName,
              'inline-flex items-center px-3 text-sm text-gray-400 border border-r-0 rounded-l-md',
              {
                'dark:border-gray-200 border-gray-200': !error && !isHovered && !isFocused,
                [boarderFocusColor]: !error && !isHovered && !isFocused,
                'dark:border-red-400 border-red-400  focus:border-red-500 bg-red-100': error,
                'border-gray-300': isHovered && !isFocused,
                [boarderColor]: isFocused && !error,
                [bgColor]: !disabled && !error,
                'bg-gray-100': disabled,
              }
            )}
          >
            {icon}
          </div>
        )}
        <input
          id={id}
          type={type}
          value={inputValue}
          min={min}
          max={max}
          step={step}
          name={name}
          autoComplete="off"
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          autoFocus={autoFocus}
          ref={inputRef}
          maxLength={maxLength}
          required={required}
          spellCheck={spellCheck}
          className={classNames(
            inputClassName,
            'block w-full text-sm border border-1 border-solid appearance-none focus:outline-none focus:ring-0 peer',
            {
              [textColor]: !error,
              'rounded-r-lg border-l-0': iconSide === UiIconSide.LEFT || iconSecondSide === UiIconSide.LEFT,
              'rounded-r-none border-r-0': iconSide === UiIconSide.RIGHT || iconSecondSide === UiIconSide.RIGHT,
              'rounded-lg': iconSide === UiIconSide.NO && iconSecondSide === UiIconSide.NO,
              [size]: true,
              [bgColor]: !error && !disabled,
              'bg-gray-100': disabled,
              'border-red-400 focus:border-red-500 text-red-500': error,
              [boarderColor]: isFocused && !error,
              [boarderFocusColor]: !error && !isHovered && !isFocused,
              [`${inputTextColorClassName}`]: !error,
              'border-gray-300': isHovered && !isFocused,
              [caret]: !error,
              'caret-red-500': error,
              'caret-gray-300': disabled,
              [cursor]: cursor,
            }
          )}
          data-cy="input"
        />
        {iconSide === UiIconSide.RIGHT && icon && (
          <div
            className={classNames(
              'inline-flex items-center px-3 text-sm text-gray-400 border border-l-0 rounded-r-md',
              {
                'border-gray-200': !error && !isHovered && !isFocused,
                [boarderFocusColor]: !error && !isHovered && !isFocused,
                'border-red-400 focus:border-red-500 bg-red-100': error,
                'border-gray-300': isHovered && !isFocused,
                [boarderColor]: isFocused && !error,
                [bgColor]: !disabled && !error,
                'bg-gray-100': disabled,
              }
            )}
          >
            {icon}
          </div>
        )}
        {iconSecondSide === UiIconSide.RIGHT && iconSecond && (
          <div
            className={classNames(
              'inline-flex items-center px-3 text-sm text-gray-400 cursor-pointer  border rounded-r-md',
              {
                'border-gray-200': !error && !isHovered && !isFocused,
                [boarderFocusColor]: !error && !isHovered && !isFocused,
                'border-red-400 focus:border-red-500 bg-red-100': error,
                'border-gray-300': isHovered && !isFocused,
                [boarderColor]: isFocused && !error,
                [bgColor]: !disabled && !error,
                'bg-gray-100': disabled,
                'rounded-l-none border-l-0': iconSide === UiIconSide.RIGHT || iconSecondSide === UiIconSide.RIGHT,
              }
            )}
            onClick={onIconSecondClick}
          >
            <div className={`${iconSecondInnerClassname} rounded-md`}>{iconSecond}</div>
          </div>
        )}
        {view === UiInputView.LABEL_BORDER && (
          <UiLabel
            id={id}
            label={label}
            disabled={disabled}
            error={error}
            view={view}
            isFocused={isFocused}
            labelSmallGrey={labelSmallGrey}
          />
        )}
      </div>
      {error && (
        <p id="outlined_success_help" className="my-2 text-xs text-red-500 ">
          <span className="font-medium">{errorText}</span>
        </p>
      )}
    </div>
  );
};
